import { motion } from "framer-motion";
import { ArrowLeft, FileText, ShieldCheck, AlertCircle, Scale, Users, Briefcase } from "lucide-react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

export default function Terms() {
  const lastUpdated = "April 20, 2026";

  const sections = [
    {
      icon: <ShieldCheck className="text-blue-500" size={24} strokeWidth={2.5} />,
      title: "1. Acceptance of Terms",
      content: "By accessing and using Info Payangadi (the 'Platform'), you agree to comply with and be bound by these Terms of Service. If you do not agree to these terms, please do not use our Platform. These terms apply to all visitors, users, job seekers, and others who access or use the Service."
    },
    {
      icon: <Users className="text-emerald-500" size={24} strokeWidth={2.5} />,
      title: "2. User Accounts & Registration",
      content: "When you create an account with us, you must provide accurate, complete, and current information. You are responsible for safeguarding the password that you use to access the Platform and for any activities or actions under your password. You must notify us immediately upon becoming aware of any breach of security or unauthorized use of your account."
    },
    {
      icon: <Briefcase className="text-amber-500" size={24} strokeWidth={2.5} />,
      title: "3. Directory & Job Listings Disclaimer",
      content: "Info Payangadi acts as a digital directory and news portal for the Kalliasseri constituency. While we strive to provide accurate job news, contact details, and emergency information, we do not guarantee the absolute accuracy, completeness, or reliability of any third-party information, job postings, or directory listings. Users are advised to independently verify all details before making decisions."
    },
    {
      icon: <AlertCircle className="text-rose-500" size={24} strokeWidth={2.5} />,
      title: "4. Prohibited Activities",
      content: "You agree not to engage in any of the following prohibited activities: (a) copying, distributing, or disclosing any part of the Platform in any medium; (b) using any automated system (spiders, robots, scrapers) to access the Platform; (c) attempting to interfere with or compromise the system integrity or security; (d) uploading invalid data, viruses, or other software agents."
    },
    {
      icon: <Scale className="text-purple-500" size={24} strokeWidth={2.5} />,
      title: "5. Limitation of Liability",
      content: "In no event shall Info Payangadi, nor its directors, employees, partners, agents, suppliers, or affiliates, be liable for any indirect, incidental, special, consequential or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from your access to or use of or inability to access or use the Platform."
    },
    {
      icon: <FileText className="text-slate-500" size={24} strokeWidth={2.5} />,
      title: "6. Changes to Terms",
      content: "We reserve the right, at our sole discretion, to modify or replace these Terms at any time. We will try to provide at least 30 days' notice prior to any new terms taking effect. By continuing to access or use our Platform after those revisions become effective, you agree to be bound by the revised terms."
    }
  ];

  return (
    <>
      <Helmet>
        <title>Terms of Service | Info Payangadi</title>
        <meta name="description" content="Terms of Service and usage guidelines for Info Payangadi." />
      </Helmet>

      <div className="min-h-[100dvh] w-full bg-[#F8FAFC] pb-24 relative overflow-hidden selection:bg-blue-200">
        
        {/* Background Glows */}
        <div className="absolute top-[-10%] left-[-10%] w-[400px] h-[400px] bg-blue-400/10 rounded-full blur-[100px] pointer-events-none"></div>
        <div className="absolute top-[40%] right-[-10%] w-[300px] h-[300px] bg-indigo-400/10 rounded-full blur-[100px] pointer-events-none"></div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-8 md:pt-12 relative z-10">
          
          {/* Back Button */}
          {/* <Link 
            to="/auth" 
            className="inline-flex items-center gap-2 text-slate-500 hover:text-blue-600 font-bold transition-colors bg-white px-4 py-2 rounded-full shadow-sm border border-slate-100 mb-8"
          >
            <ArrowLeft size={18} strokeWidth={2.5} />
            <span>Back</span>
          </Link> */}

          {/* Header */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center justify-center p-4 bg-white text-blue-600 rounded-2xl mb-4 shadow-[0_8px_30px_rgb(37,99,235,0.12)] border border-blue-50">
              <FileText size={32} strokeWidth={2.5} />
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-slate-800 tracking-tight mb-4">
              Terms of <span className="text-blue-600">Service</span>
            </h1>
            <p className="text-slate-500 font-medium max-w-2xl mx-auto">
              Please read these terms carefully before using our platform. Your access to and use of the service is conditioned on your acceptance of these terms.
            </p>
            <div className="mt-4 inline-block bg-slate-100 px-4 py-1.5 rounded-full border border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-widest">
              Last Updated: {lastUpdated}
            </div>
          </motion.div>

          {/* Content Cards */}
          <div className="space-y-6">
            {sections.map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-6 sm:p-8 rounded-[2rem] shadow-[0_8px_30px_rgb(0,0,0,0.03)] border border-slate-100 hover:shadow-[0_15px_40px_rgb(0,0,0,0.06)] transition-all duration-300"
              >
                <div className="flex flex-col sm:flex-row gap-4 sm:gap-5 items-start">
                  <div className="bg-slate-50 p-3 rounded-2xl shrink-0 border border-slate-100">
                    {section.icon}
                  </div>
                  <div>
                    <h3 className="text-xl font-black text-slate-800 mb-3 tracking-tight">
                      {section.title}
                    </h3>
                    <p className="text-slate-600 leading-relaxed font-medium text-sm sm:text-base">
                      {section.content}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Contact Section */}
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="mt-12 text-center bg-blue-600 rounded-[2rem] p-8 sm:p-12 shadow-xl shadow-blue-600/20 text-white"
          >
            <h3 className="text-2xl font-black mb-3">Questions about our Terms?</h3>
            <p className="text-blue-100 font-medium mb-6 max-w-xl mx-auto">
              If you have any questions or concerns about these Terms of Service, please contact our support team.
            </p>
            <a 
              href="mailto:payangadilive@gmail.com" 
              className="inline-flex items-center justify-center bg-white text-blue-600 font-black px-8 py-3.5 rounded-xl shadow-sm hover:bg-blue-50 hover:scale-105 transition-all active:scale-95"
            >
              Contact Support
            </a>
          </motion.div>

        </div>
      </div>
    </>
  );
}